<template>
    <div class="pdf-viewer" :class="{ fixed }">
        <div v-if="pageCount" class="actions-container actions-container-top">
            <div class="actions-container-grid">
                <flex-container class="actions-container-zoom" align="center">
                    <button @click="pdfViewZoomIn" class="pdf-button mr-5" id="prev">
                        <PlusIcon class="rotate-180" fill="#fff" />
                    </button>
                    <button @click="pdfViewZoomOut" class="pdf-button mr-5" id="prev">
                        <MinusIcon class="rotate-180" fill="#fff" />
                    </button>
                    <DefaultDropdown
                        @update="updateScale"
                        :active-case="
                            (pdfViewer &&
                                pdfViewer.currentScaleValue &&
                                scaleValues.find(v => v.id === pdfViewer.currentScaleValue)) ||
                            (pdfViewer.currentScaleValue && `${parseInt(pdfViewer.currentScaleValue * 100)}%`)
                        "
                        :cases="scaleValues"
                    />
                </flex-container>
            </div>
        </div>
        <div style="position: absolute" id="viewerContainer" class="custom-scroll">
            <div id="viewer" class="pdfViewer"></div>
        </div>
        <div v-if="loading" class="pdf-loading loading"></div>
        <div v-if="pageCount" class="actions-container">
            <div class="actions-container-grid">
                <div class="actions-container-navigation">
                    <button @click="onPrevPage" class="pdf-button arrow" id="prev">
                        <ArrowRightIcon class="rotate-180" fill="#fff" />
                    </button>
                    <span
                        ><span>{{ currentPage }}</span> / <span>{{ pageCount }}</span></span
                    >
                    <button @click="onNextPage" class="pdf-button arrow" id="next">
                        <ArrowRightIcon fill="#fff" />
                    </button>
                </div>
                <flex-container class="actions-container-zoom" align="center">
                    <button @click="pdfViewZoomIn" class="pdf-button mr-5" id="prev">
                        <PlusIcon class="rotate-180" fill="#fff" />
                    </button>
                    <button @click="pdfViewZoomOut" class="pdf-button mr-5" id="prev">
                        <MinusIcon class="rotate-180" fill="#fff" />
                    </button>
                    <DefaultDropdown
                        style-modifier="bottom"
                        @update="updateScale"
                        :active-case="
                            (pdfViewer &&
                                pdfViewer.currentScaleValue &&
                                scaleValues.find(v => v.id === pdfViewer.currentScaleValue)) ||
                            (pdfViewer.currentScaleValue && `${parseInt(pdfViewer.currentScaleValue * 100)}%`)
                        "
                        :cases="scaleValues"
                    />
                </flex-container>

                <flex-container justify="flex-end" class="">
                    <div class="relative">
                        <TooltipOnHover dark :to-top="-26" class="open" v-if="findState">
                            <flex-container align="center">
                                <default-title small :font-size="12" color="#fff" class="mt-2 mr-7"
                                    >{{ findState.current }} of {{ findState.total }} matches</default-title
                                >

                                <button @click="searchText('again', true)" class="pdf-button small" id="prev">
                                    <ArrowRightIcon class="rotate-180" fill="#fff" />
                                </button>

                                <button @click="searchText('again', false)" class="pdf-button small ml-4" id="next">
                                    <ArrowRightIcon fill="#fff" />
                                </button>
                            </flex-container>
                        </TooltipOnHover>
                        <DefaultInput placeholder="Search" bloated v-model="search" />
                    </div>
                    <div class="flex items-center">
                        <button v-if="allowDownload" @click="download" class="pdf-button ml-5">
                            <DownloadIcon width="20" fill="#fff" />
                        </button>
                        <button @click="togglePresentationMode" class="pdf-button ml-5">
                            <FullScreenIcon width="20" fill="#fff" />
                        </button>
                    </div>
                </flex-container>
            </div>
        </div>
    </div>
</template>

<script>
import FlexContainer from "@components/Containers/FlexContainer.vue"
import { DownloadManager } from "@components/Files/utils/download-manager"
import DefaultDropdown from "@components/Forms/DefaultDropdown.vue"
import DefaultInput from "@components/Forms/DefaultInput.vue"
import TooltipOnHover from "@components/Tooltips/TooltipOnHover.vue"
import DefaultTitle from "@components/Typography/DefaultTitle.vue"
import ArrowRightIcon from "@icons/ArrowRightIcon.vue"
import DownloadIcon from "@icons/DownloadIcon.vue"
import FullScreenIcon from "@icons/FullScreenIcon.vue"
import MinusIcon from "@icons/MinusIcon.vue"
import PlusIcon from "@icons/PlusIcon.vue"
import checkDevice from "@mixins/checkDevice"

const WORKER_URL = new URL("/build/js/pdf_3.worker.min.js", window.location).href
const CMAP_URL = "https://cdn.jsdelivr.net/npm/pdfjs-dist@5.3.93/cmaps/"
const CMAP_PACKED = true

const SANDBOX_BUNDLE_SRC = new URL("/build/js/pdf_3.sandbox.js", window.location)
const ENABLE_XFA = true

const DEFAULT_SCALE_DELTA = 1.04
const MIN_SCALE = 0.1
const MAX_SCALE = 10.0
const DEFAULT_SCALE_VALUE = window.innerWidth < 722 ? "page-fit" : "auto"
const DEFAULT_ZOOM_DELAY = 400

const SCALE_VALUES = [
    {
        name: "Auto Zoom",
        id: "auto"
    },
    {
        name: "Actual Size",
        id: "page-actual"
    },
    {
        name: "Page Fit",
        id: "page-fit"
    },
    {
        name: "Page Width",
        id: "page-width"
    },
    {
        name: "50%",
        id: "0.5"
    },
    {
        name: "75%",
        id: "0.75"
    },
    {
        name: "100%",
        id: "1"
    },
    {
        name: "125%",
        id: "1.25"
    },
    {
        name: "150%",
        id: "1.5"
    },
    {
        name: "200%",
        id: "2"
    },
    {
        name: "300%",
        id: "3"
    },
    {
        name: "400%",
        id: "4"
    }
]

export default {
    components: {
        DefaultTitle,
        TooltipOnHover,
        DownloadIcon,
        FullScreenIcon,
        DefaultInput,
        DefaultDropdown,
        MinusIcon,
        PlusIcon,
        FlexContainer,
        ArrowRightIcon
    },
    props: {
        path: {
            type: String,
            default: ""
        },
        allowDownload: {
            type: Boolean,
            default: false
        }
    },
    name: "PdfViewer",
    mixins: [checkDevice],
    data() {
        return {
            pdfViewer: null,
            currentPage: 1,
            pageCount: null,
            container: null,
            eventBus: null,
            currentScale: 1,
            pdfLinkService: null,
            pdfScriptingManager: null,
            loading: true,
            scaleValues: SCALE_VALUES,
            supportsMouseWheelZoomCtrlKey: true,
            supportsMouseWheelZoomMetaKey: true,
            supportsPinchToZoom: true,
            isCtrlKeyDown: false,
            caretBrowsing: null,
            pdfDocument: null,
            fixed: false,
            isScrolling: false,
            wheelUnusedTicks: 0,
            wheelUnusedFactor: 0,
            downloadManager: new DownloadManager(),
            search: "",
            isPresentationEnabled: false,
            findState: null
        }
    },
    mounted() {
        window.addEventListener("wheel", this.onWheel, {
            passive: false
        })
        setTimeout(() => {
            if (this.$el) {
                this.$el.style.minHeight = `${window.innerHeight * 0.8}px`
            }
            try {
                this.onViewerInit()
            } catch (e) {
                console.log(e)
            }
        })

        this.isPresentationEnabled =
            document.fullscreenEnabled || document.mozFullscreenEnabled || document.msFullscreenEnabled
    },
    methods: {
        getFilenameFromUrl(u) {
            const [url] = u.split(/[#?]/, 1)
            return decodeURI(url.substring(url.lastIndexOf("/") + 1))
        },
        async download() {
            let data
            try {
                data = await this.pdfDocument.getData()
            } catch {
                // When the PDF document isn't ready, simply download using the URL.
            }
            this.downloadManager.download(data, window.app_root_url_no_locale, this.getFilenameFromUrl(this.path))
        },
        isInFullScreen() {
            return (
                (document.fullscreenElement && true) ||
                (document.webkitFullscreenElement && true) ||
                (document.mozFullScreenElement && true) ||
                (document.msFullscreenElement && true)
            )
        },
        togglePresentationMode() {
            if (!this.isPresentationEnabled) {
                this.fixed = !this.fixed
                return
            }

            const el = this.$el

            if (!this.isInFullScreen()) {
                if (el.requestFullScreen) {
                    el.requestFullScreen()
                } else if (el.mozRequestFullScreen) {
                    el.mozRequestFullScreen()
                } else if (el.webkitRequestFullScreen) {
                    el.webkitRequestFullScreen()
                } else if (el.webkitRequestFullscreen) {
                    el.webkitRequestFullscreen()
                } else if (el.webkitEnterFullscreen) {
                    el.webkitEnterFullScreen()
                }
            } else {
                if (document.exitFullscreen) {
                    document.exitFullscreen()
                } else if (document.webkitExitFullscreen) {
                    document.webkitExitFullscreen()
                } else if (document.mozCancelFullScreen) {
                    document.mozCancelFullScreen()
                } else if (document.msExitFullscreen) {
                    document.msExitFullscreen()
                }
            }
        },
        searchText(type, findPrev = false) {
            if (type === "again" && this.findState) {
                if (findPrev) {
                    if (this.findState.current > 1) {
                        this.findState.current--
                    } else {
                        this.findState.current = this.findState.total
                    }
                } else {
                    if (this.findState.current !== this.findState.total) {
                        this.findState.current++
                    } else {
                        this.findState.current = 1
                    }
                }
            }

            this.eventBus.dispatch("find", {
                type,
                query: this.search,
                caseSensitive: false,
                entireWord: false,
                findPrevious: findPrev,
                highlightAll: true,
                matchDiacritics: true
            })

            setTimeout(() => {
                const el = document.querySelector(".highlight.selected")

                if (el) {
                    const top = el.getBoundingClientRect().top
                    if (top > this.container.offsetHeight) {
                        this.container.scrollTop = top - this.container.offsetHeight / 2
                    }
                }
            }, 100)
        },
        normalizeWheelEventDirection(evt) {
            let delta = Math.hypot(evt.deltaX, evt.deltaY)
            const angle = Math.atan2(evt.deltaY, evt.deltaX)
            if (-0.25 * Math.PI < angle && angle < 0.75 * Math.PI) {
                // All that is left-up oriented has to change the sign.
                delta = -delta
            }
            return delta
        },
        onWheel(evt) {
            const { pdfViewer, supportsMouseWheelZoomCtrlKey, supportsMouseWheelZoomMetaKey, supportsPinchToZoom } =
                this

            if (!pdfViewer || pdfViewer.isInPresentationMode) {
                return
            }

            // Pinch-to-zoom on a trackpad maps to a wheel event with ctrlKey set to true
            // https://developer.mozilla.org/en-US/docs/Web/API/WheelEvent#browser_compatibility
            // Hence if ctrlKey is true but ctrl key hasn't been pressed then we can
            // infer that we have a pinch-to-zoom.
            // But the ctrlKey could have been pressed outside of the browser window,
            // hence we try to do some magic to guess if the scaleFactor is likely coming
            // from a pinch-to-zoom or not.

            // It is important that we query deltaMode before delta{X,Y}, so that
            // Firefox doesn't switch to DOM_DELTA_PIXEL mode for compat with other
            // browsers, see https://bugzilla.mozilla.org/show_bug.cgi?id=1392460.
            const deltaMode = evt.deltaMode

            // The following formula is a bit strange but it comes from:
            // https://searchfox.org/mozilla-central/rev/d62c4c4d5547064487006a1506287da394b64724/widget/InputData.cpp#618-626
            let scaleFactor = Math.exp(-evt.deltaY / 100)

            const isBuiltInMac = this.isMac()
            const isPinchToZoom =
                evt.ctrlKey &&
                !this.isCtrlKeyDown &&
                deltaMode === WheelEvent.DOM_DELTA_PIXEL &&
                evt.deltaX === 0 &&
                (Math.abs(scaleFactor - 1) < 0.05 || isBuiltInMac) &&
                evt.deltaZ === 0
            const origin = [evt.clientX, evt.clientY]

            if (
                isPinchToZoom ||
                (evt.ctrlKey && supportsMouseWheelZoomCtrlKey) ||
                (evt.metaKey && supportsMouseWheelZoomMetaKey)
            ) {
                // Only zoom the pages, not the entire viewer.
                evt.preventDefault()

                // NOTE: this check must be placed *after* preventDefault.
                if (this.isScrolling || document.visibilityState === "hidden") {
                    return
                }

                if (isPinchToZoom && supportsPinchToZoom) {
                    scaleFactor = this._accumulateFactor(pdfViewer.currentScale, scaleFactor, "wheelUnusedFactor")
                    this.updateZoom(null, scaleFactor, origin)
                } else {
                    const delta = this.normalizeWheelEventDirection(evt)

                    let ticks = 0
                    if (deltaMode === WheelEvent.DOM_DELTA_LINE || deltaMode === WheelEvent.DOM_DELTA_PAGE) {
                        // For line-based devices, use one tick per event, because different
                        // OSs have different defaults for the number lines. But we generally
                        // want one "clicky" roll of the wheel (which produces one event) to
                        // adjust the zoom by one step.
                        //
                        // If we're getting fractional lines (I can't think of a scenario
                        // this might actually happen), be safe and use the accumulator.
                        ticks =
                            Math.abs(delta) >= 1 ? Math.sign(delta) : this._accumulateTicks(delta, "wheelUnusedTicks")
                    } else {
                        // pixel-based devices
                        const PIXELS_PER_LINE_SCALE = 30
                        ticks = this._accumulateTicks(delta / PIXELS_PER_LINE_SCALE, "wheelUnusedTicks")
                    }

                    this.updateZoom(ticks, null, origin)
                }
            }
        },
        _accumulateTicks(ticks, prop) {
            // If the direction changed, reset the accumulated ticks.
            if ((this[prop] > 0 && ticks < 0) || (this[prop] < 0 && ticks > 0)) {
                this[prop] = 0
            }
            this[prop] += ticks
            const wholeTicks = Math.trunc(this[prop])
            this[prop] -= wholeTicks
            return wholeTicks
        },
        _accumulateFactor(previousScale, factor, prop) {
            if (factor === 1) {
                return 1
            }
            // If the direction changed, reset the accumulated factor.
            if ((this[prop] > 1 && factor < 1) || (this[prop] < 1 && factor > 1)) {
                this[prop] = 1
            }

            const newFactor = Math.floor(previousScale * factor * this[prop] * 100) / (100 * previousScale)
            this[prop] = factor / newFactor

            return newFactor
        },
        updateScale({ id }) {
            this.pdfViewer.currentScaleValue = id
        },
        pdfViewZoomIn() {
            let newScale = this.pdfViewer.currentScale
            newScale = parseFloat((newScale * DEFAULT_SCALE_DELTA).toFixed(2))
            newScale = Math.min(MAX_SCALE, newScale)
            this.pdfViewer.currentScaleValue = newScale
        },
        updateZoom(steps, scaleFactor, origin) {
            if (this.pdfViewer.isInPresentationMode) {
                return
            }
            this.pdfViewer.updateScale({
                drawingDelay: DEFAULT_ZOOM_DELAY,
                steps,
                scaleFactor,
                origin
            })
        },
        pdfViewZoomOut() {
            let newScale = this.pdfViewer.currentScale

            newScale = parseFloat((newScale / DEFAULT_SCALE_DELTA).toFixed(2))

            newScale = Math.max(MIN_SCALE, newScale)

            this.pdfViewer.currentScaleValue = newScale
        },
        onPrevPage() {
            this.pdfViewer.currentPageNumber = this.currentPage - 1
        },
        onNextPage() {
            this.pdfViewer.currentPageNumber = this.currentPage + 1
        },
        async onViewerInit() {
            const pdfjsLib = window.pdfjsLib
            const pdfjsViewer = window.pdfjsViewer

            if (!pdfjsLib || !pdfjsViewer) {
                setTimeout(() => {
                    this.onViewerInit()
                }, 200)
                return
            }

            pdfjsLib.GlobalWorkerOptions.workerSrc = WORKER_URL

            this.container = this.$el.querySelector("#viewerContainer")

            this.eventBus = new pdfjsViewer.EventBus()

            // (Optionally) enable hyperlinks within PDF files.
            this.pdfLinkService = new pdfjsViewer.PDFLinkService({
                eventBus: this.eventBus
            })

            // (Optionally) enable find controller.
            this.pdfFindController = new pdfjsViewer.PDFFindController({
                eventBus: this.eventBus,
                linkService: this.pdfLinkService
            })

            // (Optionally) enable scripting support.
            this.pdfScriptingManager = new pdfjsViewer.PDFScriptingManager({
                eventBus: this.eventBus,
                sandboxBundleSrc: SANDBOX_BUNDLE_SRC
            })

            this.pdfViewer = new pdfjsViewer.PDFViewer({
                container: this.container,
                eventBus: this.eventBus,
                linkService: this.pdfLinkService,
                findController: this.pdfFindController,
                scriptingManager: this.pdfScriptingManager,
                annotationMode: pdfjsLib.AnnotationMode.ENABLE
            })

            this.pdfLinkService.setViewer(this.pdfViewer)
            this.pdfScriptingManager.setViewer(this.pdfViewer)

            this.eventBus.on("pagesinit", () => {
                // We can use pdfViewer now, e.g. let's change default scale.
                this.pdfViewer.currentScaleValue = DEFAULT_SCALE_VALUE
            })
            //onUpdateFindControlState

            this.eventBus.on("updatefindmatchescount", ({ matchesCount }) => {
                // We can use pdfViewer now, e.g. let's change default scale.
                this.findState = matchesCount
            })

            /*  this.eventBus.on("updatefindcontrolstate", ({ state, previous, entireWord, matchesCount, rawQuery }) => {
                // We can use pdfViewer now, e.g. let's change default scale.
                //this.findState = matchesCount
            })*/

            this.eventBus.on("pagechanging", e => {
                this.currentPage = e.pageNumber
            })

            // Loading document.
            const loadingTask = pdfjsLib.getDocument({
                url: this.path,
                cMapUrl: CMAP_URL,
                cMapPacked: CMAP_PACKED,
                enableXfa: ENABLE_XFA
            })

            this.pdfDocument = await loadingTask.promise
            // Document loaded, specifying document for the viewer and
            // the (optional) linkService.

            this.pageCount = this.pdfDocument.numPages
            this.pdfViewer.setDocument(this.pdfDocument)

            this.pdfLinkService.setDocument(this.pdfDocument, null)

            this.loading = false
        }
    },
    watch: {
        search(val) {
            //this.updateScale({ id: "page-fit" })

            this.searchText()

            if (!val) {
                this.findState = null
                //this.updateScale({ id: "auto" })
            }
        }
    }
}
</script>

<style scoped lang="sass">
.toggle-container-visible
    display: none
    @media (max-width: 722px)
        display: flex
.toggle-container
    @media (max-width: 722px)
        visibility: hidden
        pointer-events: none
        width: 50px
    @media (max-width: 440px)
        width: 0 !important
.actions-container-zoom
    //visibility: hidden
    white-space: nowrap
    @media (max-width: 722px)
        position: absolute
        display: none
.pdf-button
    width: 32px
    min-width: 32px
    height: 32px
    border: 1px solid #777777
    background: #545454
    display: flex
    align-items: center
    justify-content: center
    border-radius: 3px
    &.arrow
        svg
            stroke: none !important
    @media (max-width: 440px)
        width: 24px
        min-width: 24px
        height: 24px
        svg
            width: 16px
            height: 16px
        &.arrow
            svg
                width: 18px
                height: 18px
    &.small
        width: 20px
        height: 20px
        min-width: 20px
    &:hover
        background-color: #606060
.pdf-loading
    position: absolute
    top: 50%
    left: 50%
.pdf-viewer
    position: relative
    min-height: 70vh
    width: 100%
    margin-bottom: 32px
    @media (max-width: 722px)
        margin-bottom: 54px
        margin-top: 54px
    &:fullscreen
        .actions-container
            position: fixed
            border-radius: 0 !important
            bottom: 0
            @media (max-width: 475px)
                padding-bottom: 20px
                &.actions-container-top
                    padding-bottom: 5px
            .actions-container-grid
                max-width: 900px
        .actions-container-top
            top: 0
    &.fixed
        z-index: 999999999999999
        position: fixed
        height: 100vh
        width: 100vw
        left: 0
        top: 0
        right: 0
        bottom: 0
        .actions-container
            position: fixed
            bottom: 0
            .actions-container-grid
                max-width: 900px
    .custom-scroll::-webkit-scrollbar-track
        background-color: #eee
        width: 8px
        height: 8px

    .custom-scroll::-webkit-scrollbar-thumb
        -webkit-border-radius: 0
        border-radius: 0
        background-color: rgba(141, 141, 141, 0.7)
    &::v-deep
        .default-input
            max-width: 200px
            height: 32px
            @media (max-width: 440px)
                height: 24px

            &__search-icon
                top: 7px
                left: 8px
                width: 18px
                @media (max-width: 440px)
                    top: 2px
                    width: 16px
                path
                    fill: #fff
            &__input
                background-color: #545454 !important
                color: #fff
                border: 1px solid #777777 !important
                height: 32px
                border-radius: 3px
                text-indent: 32px !important
                font-size: 12px
                @media (max-width: 440px)
                    height: 24px
                    text-indent: 30px !important
                &::placeholder
                    color: #fff
        .default-dropdown
            z-index: 4
            min-width: 0 !important
        .default-dropdown__modal
            min-width: 140px
            top: 40px
        .default-dropdown__current
            padding: 0 10px
            font-size: 13px
            height: 32px
            min-height: 32px !important
            border: 1px solid #777777 !important
            background: #545454 !important
            color: #fff
            border-radius: 3px
            @media (max-width: 440px)
                height: 24px
                min-height: 24px !important
                font-size: 10px !important
            svg
                width: 18px
                min-width: 18px !important
                path
                    fill: #fff !important
        .loadingIcon::after
            width: 30px !important
            height: 30px !important
            top: 50% !important
            left: 50% !important
            background: url(@images/portal/loader-icon-dark.svg) center no-repeat !important
#viewerContainer
    background-color: #eee
    overflow: auto
    position: absolute
    width: 100%
    height: 100%
    padding-top: 20px
    padding-bottom: 40px

.actions-container-navigation
    display: grid
    align-items: center
    grid-template-columns: 32px 80px 32px
    @media (max-width: 440px)
        grid-template-columns: 32px 60px 32px
.actions-container-zoom
    @media (max-width: 722px)
        justify-content: flex-end !important
.actions-container-top
    display: none !important
    @media (max-width: 722px)
        border-radius: 4px 4px 0 0 !important
        display: flex !important
        top: -42px
        bottom: auto !important
        .actions-container-zoom
            display: flex !important
.actions-container
    padding: 0 10px
    position: absolute
    z-index: 4
    text-align: center
    bottom: -40px
    border-radius: 0 0 4px 4px
    background-color: rgba(#000000, 70%)
    color: #fff
    left: 0
    display: flex
    align-items: center
    justify-content: center
    right: 0
    min-height: 42px
    font-size: 15px
    @media (max-width: 722px)
        bottom: -54px
        padding: 10px
    @media (max-width: 520px)
        padding: 10px
    @media (max-width: 440px)
        min-height: 36px
        padding: 5px
    .actions-container-grid
        display: grid
        align-items: center
        grid-template-columns: 1fr 1fr 1fr
        width: 100%
        grid-gap: 10px
        @media (max-width: 722px)
            grid-template-columns: 1fr 1fr
            grid-gap: 5px
    span
        font-size: 14px
    button
        font-family: Inter, sans-serif
</style>
